<div class="detail-container">
  <!-- Header -->
  <div class="detail-header">
    <div class="getBack" (click)="onClose()" style="cursor:pointer; margin-bottom:0;">
      <i class="icon-arrow-left"></i>
      <span>{{ "forms.buttons.back" | translate }}</span>
    </div>
  </div>

  <!-- Call Summary Card -->
  <div class="call-summary-card">
    <div class="call-avatar" [ngClass]="call.status === 'answered' ? 'avatar-success' : 'avatar-danger'">
      <i [class]="call.direction === 'inbound' ? 'icon-phone-incoming' : 'icon-phone-outgoing'"></i>
    </div>
    <h5 class="contact-name">
      {{ call.contact_name || call.client?.name || '' }}
    </h5>
    <p class="contact-phone">{{ call.direction === 'outbound' ? call.to_number : call.from_number }}</p>
    <div class="call-badges">
      <span class="badge" [ngClass]="getStatusClass(call.status)">
        {{ "communication.voip.status_" + call.status | translate }}
      </span>
      <span class="badge bg-light text-dark">
        {{ call.direction === 'inbound' ? ("communication.voip.inbound" | translate) : ("communication.voip.outbound" | translate) }}
      </span>
      <span class="badge" [ngClass]="getDispositionClass(call.disposition)" *ngIf="call.disposition">
        {{ "communication.voip.disposition_" + call.disposition | translate }}
      </span>
    </div>
  </div>

  <!-- Time Details -->
  <div class="detail-section">
    <h6 class="section-title"><i class="icon-clock"></i> {{ "communication.voip.time_details" | translate }}</h6>
    <div class="detail-grid">
      <div class="detail-item">
        <span class="detail-label">{{ "communication.voip.initiated" | translate }}</span>
        <span class="detail-value">{{ formatDateTime(call.started_at) }}</span>
      </div>
      <div class="detail-item" *ngIf="call.answered_at">
        <span class="detail-label">{{ "communication.voip.answered_at" | translate }}</span>
        <span class="detail-value">{{ formatDateTime(call.answered_at) }}</span>
      </div>
      <div class="detail-item" *ngIf="call.ended_at">
        <span class="detail-label">{{ "communication.voip.ended_at" | translate }}</span>
        <span class="detail-value">{{ formatDateTime(call.ended_at) }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">{{ "communication.voip.total_duration" | translate }}</span>
        <span class="detail-value fw-bold">{{ formatDuration(call.duration || 0) }}</span>
      </div>
      <div class="detail-item" *ngIf="call.talk_time && call.talk_time > 0">
        <span class="detail-label">{{ "communication.voip.talk_time" | translate }}</span>
        <span class="detail-value">{{ formatDuration(call.talk_time) }}</span>
      </div>
      <div class="detail-item" *ngIf="call.ring_time && call.ring_time > 0">
        <span class="detail-label">{{ "communication.voip.ring_time" | translate }}</span>
        <span class="detail-value">{{ formatDuration(call.ring_time) }}</span>
      </div>
    </div>
  </div>

  <!-- Notes & Disposition -->
  <div class="detail-section" *ngIf="call.notes || call.disposition">
    <h6 class="section-title"><i class="icon-pencil"></i> {{ "communication.voip.notes" | translate }}</h6>
    <div class="notes-content" *ngIf="call.notes">
      <p>{{ call.notes }}</p>
    </div>
    <div class="tags-row" *ngIf="call.tags.length > 0">
      <span class="badge bg-light text-dark me-1" *ngFor="let tag of call.tags">
        <i class="icon-tag"></i> {{ tag }}
      </span>
    </div>
    <div class="follow-up" *ngIf="call.follow_up_at">
      <i class="icon-calendar-check"></i>
      <span>{{ "communication.voip.follow_up" | translate }}: {{ formatDateTime(call.follow_up_at) }}</span>
    </div>
  </div>

  <!-- Linked references -->
  <div class="detail-section" *ngIf="call.order || call.client">
    <h6 class="section-title"><i class="icon-link"></i> {{ "communication.voip.linked_to" | translate }}</h6>
    <div class="linked-items">
      <div class="linked-item" *ngIf="call.order">
        <i class="icon-clipboard-check text-info"></i>
        <span>{{ "communication.voip.order" | translate }}: {{ call.order.reference }}</span>
      </div>
      <div class="linked-item" *ngIf="call.client">
        <i class="icon-user text-success"></i>
        <span>{{ "communication.voip.client" | translate }}: {{ call.client.name }}</span>
      </div>
    </div>
  </div>

  <!-- Recording Player -->
  <div class="detail-section" *ngIf="call.has_recording">
    <h6 class="section-title"><i class="icon-mic"></i> {{ "communication.voip.recording" | translate }}</h6>

    <div class="recording-player-real">
      <!-- Hidden audio element -->
      <audio #audioPlayer
        *ngIf="recordingUrl"
        [src]="recordingUrl"
        (play)="onAudioPlay()"
        (pause)="onAudioPause()"
        (ended)="onAudioEnded()"
        (timeupdate)="onAudioTimeUpdate()"
        (error)="onAudioError()">
      </audio>

      <div class="player-controls">
        <!-- Play/Pause button -->
        <button class="btn-play-real" (click)="playRecording()" [disabled]="recordingLoading">
          <span class="spinner-border spinner-border-sm" *ngIf="recordingLoading"></span>
          <i *ngIf="!recordingLoading" [class]="isPlaying ? 'icon-pause' : 'icon-play'"></i>
        </button>

        <!-- Progress bar -->
        <div class="player-progress" #progressBar (click)="seekTo($event, progressBar)">
          <div class="progress-filled" [style.width.%]="progressPercent"></div>
        </div>

        <!-- Time -->
        <span class="player-time">
          {{ formatDuration(playbackTime) }} / {{ formatDuration(call.recording?.duration || 0) }}
        </span>
      </div>

      <!-- Error state -->
      <div class="recording-error" *ngIf="recordingError">
        <i class="icon-warning text-danger"></i>
        <span>{{ "communication.voip.recording_unavailable" | translate }}</span>
      </div>
    </div>
  </div>

  <!-- Recording pending -->
  <div class="detail-section" *ngIf="!call.has_recording && call.recording?.status === 'pending'">
    <h6 class="section-title"><i class="icon-mic"></i> {{ "communication.voip.recording" | translate }}</h6>
    <div class="recording-pending">
      <div class="spinner-border spinner-border-sm text-primary me-2"></div>
      <span>{{ "communication.voip.recording_processing" | translate }}</span>
    </div>
  </div>

  <!-- Transcript -->
  <div class="detail-section" *ngIf="call.transcript_status">
    <h6 class="section-title"><i class="icon-text-format"></i> {{ "communication.voip.transcription" | translate }}</h6>

    <!-- Queued -->
    <div class="transcript-status" *ngIf="call.transcript_status === 'queued' && !transcript?.sentences?.length">
      <div class="spinner-border spinner-border-sm text-primary me-2"></div>
      <span>{{ "communication.voip.transcript_processing" | translate }}</span>
    </div>

    <!-- Failed -->
    <div class="transcript-status" *ngIf="call.transcript_status === 'failed'">
      <i class="icon-warning text-danger me-1"></i>
      <span>{{ "communication.voip.transcript_failed" | translate }}</span>
    </div>

    <!-- Skipped -->
    <div class="transcript-status" *ngIf="call.transcript_status === 'skipped'">
      <span class="text-muted">{{ "communication.voip.transcript_skipped" | translate }}</span>
    </div>

    <!-- Completed — sentence list -->
    <div class="transcript-sentences" *ngIf="transcript?.sentences?.length">
      <div class="transcript-lang" *ngIf="transcript?.language">
        <span class="badge bg-light text-dark">{{ transcript!.language }}</span>
      </div>
      <div class="transcript-sentence"
        *ngFor="let sentence of transcript!.sentences; let i = index"
        [class.active]="i === activeSentenceIndex"
        [class.speaker-agent]="sentence.speaker === 'agent'"
        [class.speaker-customer]="sentence.speaker === 'customer'"
        (click)="seekToSentence(sentence)">
        <div class="sentence-meta">
          <span class="speaker-badge" [class.agent]="sentence.speaker === 'agent'">
            {{ sentence.speaker === 'agent' ? '🎧' : '👤' }}
          </span>
          <span class="sentence-time">{{ formatMs(sentence.start_ms) }}</span>
        </div>
        <p class="sentence-text">{{ sentence.text }}</p>
      </div>
    </div>

    <!-- Manual refresh button (after 2min polling) -->
    <button class="btn btn-sm btn-outline-primary mt-2"
      *ngIf="call.transcript_status === 'queued' && !transcriptPolling"
      (click)="refreshTranscript()"
      [disabled]="transcriptLoading">
      <i class="icon-sync"></i> {{ "communication.voip.transcript_refresh" | translate }}
    </button>
  </div>

  <!-- Agent -->
  <div class="detail-section" *ngIf="call.agent">
    <h6 class="section-title"><i class="icon-user"></i> {{ "communication.voip.agent" | translate }}</h6>
    <div class="agent-info">
      <div class="agent-avatar">{{ call.agent.name.charAt(0) }}</div>
      <span>{{ call.agent.name }}</span>
    </div>
  </div>
</div>
